import React, { useState } from "react";
import { Link } from "react-router-dom";
import IconButton from "@mui/material/IconButton";
import ClearIcon from "@mui/icons-material/Clear";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import ListItemIcon from "@mui/material/ListItemIcon";
import Collapse from "@mui/material/Collapse";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import GroupIcon from "@mui/icons-material/Group";
import { makeStyles } from "@mui/styles";

import last from "lodash/last";
import ResetPasswordDialog from "containers/ResetPasswordDialog";

const DEFAULT_DASHBOARD_TITLE = "UNNAMED DASHBOARD";
const DEFAULT_DASHBOARD_PATH_LABEL = "UNNAMED";

const useStyles = makeStyles((theme) => ({
  title: {
    paddingRight: theme.spacing(1),
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  text: {
    fontSize: "0.875rem",
  },
  ctext: {
    fontWeight: "bold",
    fontSize: "0.875rem",
  },
  nested: {
    paddingLeft: theme.spacing(4),
  },
  close: {
    position: "absolute",
    right: theme.spacing(1),
    top: theme.spacing(1),
  },
  cancel: {
    color: "red",
  },
}));

const DashboardsNestedList = ({ node, removeDialog }) => {
  const [open, setOpen] = useState(false);

  const classes = useStyles();

  const toggle = () => setOpen(!open);

  return (
    <>
      <ListItem divider button onClick={toggle}>
        <ListItemText
          primary={node.label}
          classes={{
            primary: classes.ctext,
          }}
        />
        {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      </ListItem>
      <Collapse in={open} timeout="auto" className={classes.nested}>
        <DashboardsList nodes={node.children} removeDialog={removeDialog} />
      </Collapse>
    </>
  );
};

const DashboardsList = ({ nodes, removeDialog }) => {
  const classes = useStyles();
  return (
    <List>
      {Object.values(nodes)
        .sort((a, b) => (a.label < b.label ? -1 : 1))
        .map((cnode) => {
          if (cnode.type === "inner") {
            return (
              <DashboardsNestedList
                key={`$row-${cnode.label}`}
                node={cnode}
                removeDialog={removeDialog}
              />
            );
          } else {
            const dashboard = cnode.dashboard;
            return (
              <ListItem
                key={`$row-${cnode.label}`}
                divider
                button={true}
                component={Link}
                onClick={(event) => {
                  removeDialog();
                }}
                to={"/dashboard/" + dashboard.guuid}
              >
                {cnode.shared && (
                  <ListItemIcon>
                    <GroupIcon />
                  </ListItemIcon>
                )}
                <ListItemText
                  primary={cnode.label}
                  classes={{
                    primary: classes.text,
                  }}
                />
              </ListItem>
            );
          }
        })}
    </List>
  );
};

const ReseetPasswordDialog = (props) => {
  const classes = useStyles();

  // avoid overhead of render if not going to be visible
  if (!props.open) {
    return null;
  }

  const dashboards = Object.values(props.dashboards);
  const user = props.user;
  const dashboardHierarchy = {};
  dashboards.forEach((dashboard) => {
    let nodes = dashboardHierarchy;
    // convert to all uppercase for case-insensitivity and display
    const title = (dashboard.title || "").toUpperCase();
    const path = title.split(".");
    path.slice(0, -1).forEach((el) => {
      // trim leading and trailing whitespace, and replace all
      // remaining multiple whitespace with single space
      const label =
        el.trim().replace(/\s+/g, " ") || DEFAULT_DASHBOARD_PATH_LABEL;
      const key = `i-${label}`;
      let next = nodes[key];
      if (!next) {
        next = nodes[key] = { label, type: "inner", children: {} };
      }
      nodes = next.children;
    });
    nodes[`l-${dashboard.guuid}`] = {
      label: last(path) || DEFAULT_DASHBOARD_TITLE,
      type: "leaf",
      shared: user.userid !== dashboard.g_owner,
      dashboard,
    };
  });

  return (
    <Dialog fullWidth={true} open={props.open}>
      <DialogTitle>
        Select Dashboard
        {props.removeDialog && (
          <IconButton
            onClick={props.removeDialog}
            className={classes.close}
            size="large"
          >
            <ClearIcon />
          </IconButton>
        )}
      </DialogTitle>
      <DialogContent dividers>
        <DashboardsList
          nodes={dashboardHierarchy}
          removeDialog={props.removeDialog}
        />
      </DialogContent>
      <DialogActions>
        <Button
          variant="contained"
          color="primary"
          onClick={(event) => {
            props.addDashboard();
            props.removeDialog();
          }}
        >
          Add a new dashboard
        </Button>
        {props.removeDialog && (
          <Button onClick={props.removeDialog} style={{ color: "red" }}>
            Cancel
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ResetPasswordDialog;
